import type { UtilityBoxConfig } from '~/config/utilityBoxes'
import type { ToolManagerRef } from '../ToolManager'
import { forwardRef, useImperativeHandle, useState } from 'react'
import { useProject, useWallSegmentEdgeSnapLines } from '../hooks'
import { Vector3 } from 'three'
import { snapToLine } from '../util/snaplines'
import { useAppDispatch, useAppSelector } from '~/store/hooks'
import { SNAP_THRESHOLD, SnapPreview } from '.'
import { addUtilityBox } from '~/store/objects'
import { guid } from '~/lib/utils'
import { modelToUI, vectorUIToModel } from '../util/units'
import { parallelVectorOfWallSegment } from '../util/walls'
import LayerKeys from '~/config/layerKeys'

export const UtilityBoxTool = forwardRef<ToolManagerRef, { activeToolProps: UtilityBoxConfig }>(function UtilityBoxTool(
  { activeToolProps: props },
  ref
) {
  const project = useProject()
  const [[x, y], setPoint] = useState([0, 0])
  const snaplines = useWallSegmentEdgeSnapLines()
  const snapTarget = new Vector3(x, y, 0)
  const snapPoint = snapToLine(snaplines, snapTarget)
  const snapWallSegment = useAppSelector(it =>
    snapPoint?.objectID ? it.objects.present.segments[snapPoint.objectID] : undefined
  )
  const dispatch = useAppDispatch()

  useImperativeHandle(ref, () => ({
    onMouseDown() {
      return true
    },
    onMouseMove(event) {
      const point = project(event)
      if (point) {
        setPoint([point.x, point.y])
      }
      return true
    },
    onMouseUp() {
      if (snapPoint === undefined) {
        return true
      }

      const position = (snapPoint.distance <= SNAP_THRESHOLD ? snapPoint.snapPoint : snapTarget)
        .clone()
        .setZ(modelToUI(snapWallSegment?.height ?? 48))
      const storePosition = vectorUIToModel(position)

      const snapRotation = parallelVectorOfWallSegment(snapWallSegment!)

      dispatch(
        addUtilityBox({
          utilityBox: {
            id: guid(),
            ...props,
            handleHeight: modelToUI(200 * 12),
            position: { x: storePosition.x, y: storePosition.y, z: storePosition.z },
            rotation: { x: snapRotation.x, y: snapRotation.y, z: snapRotation.z },
            layerKey: LayerKeys.UTILITY_BOXES,
          },
        })
      )

      return true
    },
  }))

  return <>{snapPoint && <SnapPreview snapPoint={snapPoint} utilityBox={props} mousePoint={snapTarget} />}</>
})
